import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import EntitiesWithLedgerEffectsOrWithParents from "@/utils/constants/EntitiesWithLedgerEffectsOrWithParents.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import Translator from "@/utils/common/Translator.ts";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs.ts";
import Operator from "@/utils/constants/Operator.ts";
import ExpressionRelationship from "@/utils/constants/ExpressionRelationship.ts";
import {NaMaTreeNode} from "@/utils/presenters/interfaces.ts";
import {QTreeNode} from "quasar";
import DTOSearchResultSettings = namaTypes.DTOSearchResultSettings;
import SearchRequest = namaTypes.SearchRequest;
import DTOCriteria = namaTypes.DTOCriteria;

export default class TreeModelProvider {
  entityType: string;
  listedEntityType?: string
  columnsToGet: string[];
  private code: string = CommonFieldIds.CODE;
  private name1: string = CommonFieldIds.NAME1;
  private name2: string = CommonFieldIds.NAME2;
  private id: string = CommonFieldIds.ID;
  private parent: string = CommonFieldIds.PARENT;
  private parentId: string = this.parent + '.' + this.id;
  private orderBy: string = this.parentId;
  private IS_LEAF: string = "isLeaf";

  constructor(entityType: string, listedEntityType?: string) {
    this.entityType = entityType;
    this.listedEntityType = listedEntityType;
    this.columnsToGet = [this.id, this.code, this.name1, this.name2, this.parentId];
    if (TreeModelProvider.entityHasIsLeafProperty(entityType))
      this.columnsToGet.push(this.IS_LEAF);
  }

  private static entityHasIsLeafProperty(entityType: string): boolean {
    return EntitiesWithLedgerEffectsOrWithParents.HAS_IS_LEAF_ENTITIES.contains(entityType);
  }

  async getNodes(): Promise<QTreeNode<NaMaTreeNode>[]> {
    let request = this.createRequest();
    let response = await NamaCommon.namaFetch(NamaURLS.Search, {data: NamaCommon.stringify(request)})
    if (response && response.list)
      return this.groupInAllRootIfNeeded(this.createNodes(response));
    return [];
  }

  private createNodes(result): QTreeNode<NaMaTreeNode>[] {
    let page: Record<any, any>[] = result.list;
    let nodesMap: Record<string, QTreeNode<NaMaTreeNode>> = {};
    let roots: QTreeNode<NaMaTreeNode>[] = [];
    for (let pageObject of page) {
      let object: Record<any, any> = pageObject.values;
      if (!nodesMap[object["id"]]) {
        let node: QTreeNode<NaMaTreeNode> = {
          id: object["id"],
          code: Translator.calcByLanguage(object["code"], object["altCode"]),
          label: Translator.getNameForFields(object),
          isLeaf: object["isLeaf"]
        };
        if (object["parent.id"])
          node.parentId = object["parent.id"];
        else
          roots.push(node);
        nodesMap[node.id!] = node;
      }
    }
    for (let nodeId in nodesMap) {
      {
        let node: QTreeNode<NaMaTreeNode> = nodesMap[nodeId];
        if (node.parentId) {
          let actualParent: QTreeNode<NaMaTreeNode> = nodesMap[node.parentId];
          if (actualParent)
            TreeModelProvider.addChild(actualParent, node);
        }
      }
    }
    return roots;
  }

  private groupInAllRootIfNeeded(nodes: QTreeNode<NaMaTreeNode>[]): QTreeNode<NaMaTreeNode>[] {
    if (nodes && nodes.length == 1)
      return nodes;
    let roots: QTreeNode<NaMaTreeNode>[] = [];
    let root: QTreeNode<NaMaTreeNode> = {
      id: "",
      code: Translator.translateId('theAll'),
      label: Translator.translateId('theAll'),
      isLeaf: false
    };
    roots.push(root);
    if (nodes) {
      for (let node of nodes) {
        TreeModelProvider.addChild(root, node);
      }
    }
    return roots;
  }

  private createRequest(): SearchRequest {
    let columns: string[] = this.columnsToGet;
    let searchResultSettings: DTOSearchResultSettings = {
      pageData: {pageNumber: 0, pageSize: -1}, sortByColumn: this.orderBy, columnsToGet: columns
    };
    let request: SearchRequest = {entityType: this.entityType, criteria: this.createCriteria(), searchResultSettings: searchResultSettings};
    return request;
  }

  private createCriteria(): DTOCriteria {
    let criteria: DTOCriteria = {};
    criteria.expressions = [];
    if (this.listedEntityType) {
      criteria.expressions.push({
        field: DomainBaseFieldIDs.MasterGroup_forType,
        operator: Operator.Equal,
        rightHandSide: this.listedEntityType,
        relation: ExpressionRelationship.AND
      });
    }
    return criteria;
  }

  private static addChild(parent: QTreeNode<NaMaTreeNode>, node: QTreeNode<NaMaTreeNode>) {
    if (!parent.children)
      parent.children = [];
    let childAdded = false;
    for (let i = 0; i < parent.children.length; i++) {
      if (parent.children[i].code!.toLowerCase().localeCompare(node.code!.toLowerCase()) >= 0) {
        childAdded = true;
        parent.children.splice(i, 0, node);
        break;
      }
    }
    if (!childAdded)
      parent.children.push(node);
    node.parentId = parent.id;
  }
}
